import React, { useMemo, useState } from "react";
import { parseRuNumber } from "../utils/number";

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50
};
const modal = { background: "#fff", borderRadius: 12, overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.18)" };
const row = { display: "grid", gap: 12, alignItems: "center", marginBottom: 10 };
const input = { padding: 8, border: "1px solid #ddd", borderRadius: 6, width: "100%" };
const btn = { padding: "8px 12px", border: "1px solid #ddd", background: "#fff", borderRadius: 6, cursor: "pointer" };

// утилита-валидатор для onBeforeInput / onKeyDown
function allowDigitComma(e) {
  const k = e.key;
  const isControl =
    k === "Backspace" || k === "Delete" || k === "ArrowLeft" || k === "ArrowRight" ||
    k === "Tab" || e.ctrlKey || e.metaKey;
  if (isControl) return;

  // Разрешаем цифры и запятую
  if (!/[0-9,]/.test(k)) {
    e.preventDefault();
    return;
  }

  // не даём вторую запятую
  const target = e.target;
  const value = target.value;
  const selection = value.slice(0, target.selectionStart) + k + value.slice(target.selectionEnd);
  if ((k === "," && value.includes(",")) || (selection.match(/,/g)?.length > 1)) {
    e.preventDefault();
  }
}

// авто-замена точки на запятую во время ввода
function dotToComma(e, setValue) {
  const v = e.target.value.replace(/\./g, ",").replace(/[^0-9,]/g, "");
  // только одна запятая
  const parts = v.split(",");
  const norm = parts.length > 1 ? parts[0] + "," + parts.slice(1).join("").replace(/,/g, "") : v;
  setValue(norm);
}

export default function AddProductModal({ open, onClose, onSubmit, types, subtypes = [], allergens = [] }) {
  const [name, setName] = useState("");
  const [typeId, setTypeId] = useState("");
  const [subtypeId, setSubtypeId] = useState("");
  const [newType, setNewType] = useState("");
  const [newSubtype, setNewSubtype] = useState("");
  const [isChildAllowed, setIsChildAllowed] = useState(false);
  const [containsAllergens, setContainsAllergens] = useState(false);
  const [selectedAllergenIds, setSelectedAllergenIds] = useState([]);
  const [form, setForm] = useState({
    protein_g: "",
    fats_g: "",
    carbs_g: "",
    energy_kcal: "",
    fiber_g: "",
    mds_g: "",
    starch_g: "",
    water_g: "",
    na_mg: "",
    k_mg: "",
    ca_mg: "",
    mg_mg: "",
    p_mg: "",
    fe_mg: "",
    ash_g: "",
    a_mg: "",
    beta_carotene_mg: "",
    b1_mg: "",
    b2_mg: "",
    pp_mg: "",
    c_mg: "",
    retinol_index: "",
    tocopherol_index: "",
    niacin_index: "",
    nlc_g: "",
    pufa_g: "",
    cholesterol_g: "",
    organic_acids_g: "",
    alcohol_pct: ""
  });

  const canSubmit = useMemo(() => {
    const hasType = (typeId && typeId !== "__new__") || (newType.trim().length > 0);
    const allergensSelected = !containsAllergens || selectedAllergenIds.length > 0;
    return name.trim().length > 0 && hasType && allergensSelected;
  }, [containsAllergens, name, newType, selectedAllergenIds.length, typeId]);

  const availableSubtypes = useMemo(() => (
    subtypes.filter((subtype) => String(subtype.typeId) === String(typeId))
  ), [subtypes, typeId]);

  if (!open) return null;

  const submit = () => {
    // локальный уникальный id: отрицательный timestamp (чтобы не конфликтовал с серверными положительными id)
    const id = -Date.now();

    const finalTypeName = typeId === "__new__" ? newType.trim() : (types.find(t => String(t.id) === String(typeId))?.name || "");
    const finalTypeId   = typeId === "__new__" ? -Math.floor(Math.random() * 1e9) : (typeId ? Number(typeId) : null);
    const selectedSubtype = subtypes.find((subtype) => String(subtype.id) === String(subtypeId));
    const finalSubtypeName = typeId === "__new__" ? newSubtype.trim() : selectedSubtype?.name || null;
    const finalSubtypeId = typeId === "__new__"
      ? (finalSubtypeName ? -Math.floor(Math.random() * 1e9) : null)
      : (selectedSubtype ? Number(selectedSubtype.id) : null);

    const payload = {
      id,
      name: name.trim(),
      typeId: finalTypeId,
      typeName: finalTypeName || `Группа #${finalTypeId ?? "?"}`,
      subtypeId: finalSubtypeId,
      subtypeName: finalSubtypeName,
      isComplex: false,
      isAllergen: containsAllergens,
      isChildAllowed,
      allergens: containsAllergens
        ? allergens
          .filter((allergen) => selectedAllergenIds.includes(allergen.id))
          .map((allergen) => ({ ...allergen, scope: "product" }))
        : [],
      protein_g:    parseRuNumber(form.protein_g),
      fats_g:       parseRuNumber(form.fats_g),
      carbs_g:      parseRuNumber(form.carbs_g),
      energy_kcal:  parseRuNumber(form.energy_kcal),
      fiber_g:      parseRuNumber(form.fiber_g),
      dietary_fiber_g: parseRuNumber(form.fiber_g),
      mds_g:        parseRuNumber(form.mds_g),
      starch_g:     parseRuNumber(form.starch_g),
      water_g:      parseRuNumber(form.water_g),
      na_mg:        parseRuNumber(form.na_mg),
      k_mg:         parseRuNumber(form.k_mg),
      ca_mg:        parseRuNumber(form.ca_mg),
      mg_mg:        parseRuNumber(form.mg_mg),
      p_mg:         parseRuNumber(form.p_mg),
      fe_mg:        parseRuNumber(form.fe_mg),
      ash_g:        parseRuNumber(form.ash_g),
      a_mg:         parseRuNumber(form.a_mg),
      beta_carotene_mg: parseRuNumber(form.beta_carotene_mg),
      b1_mg:        parseRuNumber(form.b1_mg),
      b2_mg:        parseRuNumber(form.b2_mg),
      pp_mg:        parseRuNumber(form.pp_mg),
      c_mg:         parseRuNumber(form.c_mg),
      retinol_index: parseRuNumber(form.retinol_index),
      tocopherol_index: parseRuNumber(form.tocopherol_index),
      niacin_index: parseRuNumber(form.niacin_index),
      nlc_g:        parseRuNumber(form.nlc_g),
      pufa_g:       parseRuNumber(form.pufa_g),
      cholesterol_g:parseRuNumber(form.cholesterol_g),
      organic_acids_g: parseRuNumber(form.organic_acids_g),
      alcohol_pct:  parseRuNumber(form.alcohol_pct)
    };

    onSubmit(payload);
    onClose();
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} className="app-form-modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Добавить продукт локально</h2>
        <div style={{ marginBottom: 12, color: "#666", fontSize: 13 }}>
          Запись сохранится только в локальной базе браузера и не изменит общий серверный справочник.
        </div>

        <div style={row} className="app-form-row">
          <label>Название</label>
          <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Творог 5%" />
        </div>

        <div style={row} className="app-form-row">
          <label>Группа продукта</label>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              style={{ ...input, width: "60%" }}
              value={typeId}
              onChange={(e) => {
                setTypeId(e.target.value);
                setSubtypeId("");
                setNewSubtype("");
              }}
            >
              <option value="">— выбрать —</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
              <option value="__new__">+ Новая группа…</option>
            </select>
            {typeId === "__new__" && (
              <input style={{ ...input, width: "40%" }} value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="Введите новую группу" />
            )}
          </div>
        </div>

        <div style={row} className="app-form-row">
          <label>Подгруппа продукта</label>
          <select
            style={input}
            value={subtypeId}
            onChange={(e) => setSubtypeId(e.target.value)}
            disabled={!typeId || typeId === "__new__" || availableSubtypes.length === 0}
          >
            <option value="">
              {!typeId || typeId === "__new__"
                ? "— сначала выберите группу —"
                : availableSubtypes.length === 0
                  ? "— в этой группе нет подгрупп —"
                  : "— выбрать —"}
            </option>
            {availableSubtypes.map((subtype) => (
              <option key={subtype.id} value={subtype.id}>{subtype.name}</option>
            ))}
          </select>
        </div>

        {typeId === "__new__" && (
          <div style={row} className="app-form-row">
            <label>Новая подгруппа <span style={{ color: "#666", fontWeight: 400 }}>(необязательно)</span></label>
            <input
              style={input}
              value={newSubtype}
              onChange={(e) => setNewSubtype(e.target.value)}
              placeholder="Например: Кисломолочные продукты"
            />
          </div>
        )}

        <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={isChildAllowed}
              onChange={(e) => setIsChildAllowed(e.target.checked)}
            />
            Для детского питания
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={containsAllergens}
              onChange={(e) => {
                setContainsAllergens(e.target.checked);
                if (!e.target.checked) setSelectedAllergenIds([]);
              }}
            />
            Содержит аллергены
          </label>
          {containsAllergens && (
            <section style={{ display: "grid", gap: 8, padding: 12, border: "1px solid #e2e7df", borderRadius: 8 }}>
              <div style={{ fontWeight: 600 }}>Укажите аллергены продукта</div>
              {allergens.length === 0 ? (
                <div style={{ color: "#666", fontSize: 13 }}>Список аллергенов загружается…</div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {allergens.map((allergen) => (
                    <label key={allergen.id} style={{ border: "1px solid #dfe5dc", borderRadius: 999, padding: "6px 10px", background: "#fff" }}>
                      <input
                        type="checkbox"
                        checked={selectedAllergenIds.includes(allergen.id)}
                        onChange={(e) => setSelectedAllergenIds((current) => (
                          e.target.checked
                            ? [...current, allergen.id]
                            : current.filter((id) => id !== allergen.id)
                        ))}
                      /> {allergen.name}
                    </label>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        <hr style={{ margin: "12px 0", border: 0, borderTop: "1px solid #eee" }} />

        {/* Базовые пищевые вещества */}
        <div style={{ fontWeight: 600, lineHeight: 1.4 }}>
          Содержание пищевых веществ указывается на 100 г продукта.
        </div>
        {[
          ["protein_g",   "Белки, г"],
          ["fats_g",      "Жиры, г"],
          ["carbs_g",     "Углеводы, г"],
          ["energy_kcal", "Энергия, ккал"],
          ["fiber_g",     "Пищ. волокна, г"],
          ["mds_g",       "МДС, г"],
          ["starch_g",    "Крахмал, г"],
          ["water_g",     "Вода, г"],
          ["na_mg",       "Содержание Na, мг"],
          ["k_mg",        "Содеражние K, мг"],
          ["ca_mg",       "Содержание Ca, мг"],
          ["mg_mg",       "Содержание Mg, мг"],
          ["p_mg",        "Содержание P, мг"],
          ["fe_mg",       "Содержание Fe, мг"],
          ["ash_g",       "Содержание золы, г"],
          ["a_mg",        "Витамин A, мг"],
          ["beta_carotene_mg", "Бета-каротин, мг"],
          ["b1_mg",       "Витамин B1, мг"],
          ["b2_mg",       "Витамин B2, мг"],
          ["pp_mg",       "Витамин PP, мг"],
          ["c_mg",        "Витамин C, мг"],
          ["retinol_index", "Ретиноловый эквив., мг"],
          ["tocopherol_index", "Токофероловый эквив., мг"],
          ["niacin_index", "Ниациновый эквив., мг"],
          ["nlc_g",       "Насыщенные жирные кислоты, г"],
          ["pufa_g",      "Полиненасыщенные жирные кислоты, г"],
          ["cholesterol_g", "Холестерин, мг"],
          ["organic_acids_g", "Органические кислоты, г"],
          ["alcohol_pct", "Алкоголь, %"],
        ].map(([key, label]) => (
          <div key={key} style={row} className="app-form-row">
            <label>{label}</label>
            <input
              style={input}
              inputMode="decimal"
              value={form[key]}
              onChange={(e) => dotToComma(e, (val) => setForm({ ...form, [key]: val }))}
              onKeyDown={allowDigitComma}
              placeholder="например: 3,5"
            />
          </div>
        ))}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" style={btn} onClick={onClose}>Отмена</button>
          <button
            type="button"
            style={{ ...btn, borderColor: "#2e7d32" }}
            onClick={submit}
            disabled={!canSubmit}
            title={!canSubmit ? "Заполните название, группу и при необходимости укажите аллергены" : "Добавить"}
          >
            Добавить
          </button>
        </div>
      </div>
    </div>
  );
}
